'use client';

import React, { useEffect, useState } from 'react';
import { MultiStepForm } from '@/app/components/form/MultiStepForm';
import { Navbar } from '@/app/components/Navbar';
import { EligibilityCheck } from '@/app/components/EligibilityCheck';
import { useWillStore } from '@/lib/store';
import { createClient } from '@/lib/supabase/client';
import { Button } from '@/app/components/ui/Button';
import { Skeleton } from '@/app/components/ui/Skeleton';
import Link from 'next/link';
import { ConflictModal } from '@/app/components/ConflictModal';
import { PaymentSuccessModal } from '@/app/components/PaymentSuccessModal';
import { transformToFlat } from '@/lib/transformers';

export function ClientPage() {
  const { isEligible, isCapable, isSuitable, isAuthenticated, setEligibility, setCapability, setSuitability, setAuth, setGuestAccess, clearAuth, setData, data } = useWillStore();
  const [isHydrated, setIsHydrated] = useState(() => {
    const persistApi = (useWillStore as any).persist;
    return persistApi?.hasHydrated?.() ?? true;
  });
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictRemoteData, setConflictRemoteData] = useState<any>(null);
  const [conflictRemoteWillId, setConflictRemoteWillId] = useState<string>('');
  const [conflictRemoteDate, setConflictRemoteDate] = useState<string>('');
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    // Access persist API directly inside effect to avoid dependency array issues.
    const persistApi = (useWillStore as any).persist;
    const unsub = persistApi?.onFinishHydration?.(() => setIsHydrated(true));
    return () => unsub?.();
  }, []);

  // Centralized logic to setup user data after auth
  const setupUser = async (user: any) => {
    const supabase = createClient();

    // Get stored signup data if available (from magic link flow)
    const storedName = localStorage.getItem('will_signup_name');
    const storedOptOut = localStorage.getItem('will_signup_marketing_opt_out') === 'true';
    let storedEligibility = null;
    try {
      const stored = localStorage.getItem('will_signup_eligibility');
      if (stored) storedEligibility = JSON.parse(stored);
    } catch (e) {
      // Ignore
    }

    // Clean up localStorage
    localStorage.removeItem('will_signup_name');
    localStorage.removeItem('will_signup_marketing_opt_out');
    localStorage.removeItem('will_signup_eligibility');

    // Create/update customer profile if we have stored data or metadata
    const displayName = storedName || user.user_metadata?.full_name;

    if (displayName) {
      await supabase
        .from('customers')
        .upsert({
          id: user.id,
          display_name: displayName,
          marketing_opt_out: storedOptOut,
        }, {
          onConflict: 'id',
        });
    }

    // Handle Supabase invite flow - check if user metadata contains a willId
    // This happens when partner accepts a mirror will invite
    const inviteWillId = user.user_metadata?.willId;
    if (inviteWillId) {
      console.log('[Setup User] Mirror invite detected, attaching will:', inviteWillId);

      // Attach the will to this user
      const { error: attachError } = await supabase
        .from('wills')
        .update({ user_id: user.id })
        .eq('id', inviteWillId)
        .is('user_id', null); // Only attach if not already claimed

      if (attachError) {
        console.error('Failed to attach invite will:', attachError);
      } else {
        console.log('[Setup User] Successfully attached will to user');
      }
    }

    // Check for existing will (draft or paid). Prefer the user's current willId,
    // otherwise fall back to the most recently updated will. If the latest record
    // is the partner side of a mirror pair, load the primary will to avoid
    // triggering conflicts when returning from payment with a mirror will.
    const baseWillSelect = 'id, will_data, current_step, completed_steps, status, updated_at, mirror_role, paired_will_id';
    const localWillId = useWillStore.getState().willId;
    let willRecord: any = null;

    if (localWillId) {
      const { data: matchingWill } = await supabase
        .from('wills')
        .select(baseWillSelect)
        .eq('id', localWillId)
        .maybeSingle();

      if (matchingWill) {
        willRecord = matchingWill;
      }
    }

    if (!willRecord) {
      const { data: existingWills } = await supabase
        .from('wills')
        .select(baseWillSelect)
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(1);

      if (existingWills && existingWills.length > 0) {
        willRecord = existingWills[0];

        if (willRecord.mirror_role === 'partner' && willRecord.paired_will_id) {
          const { data: pairedWill } = await supabase
            .from('wills')
            .select(baseWillSelect)
            .eq('id', willRecord.paired_will_id)
            .maybeSingle();

          if (pairedWill) {
            willRecord = pairedWill;
          }
        }
      }
    }

    let willId: string;

    if (willRecord) {
      // Use existing will
      willId = willRecord.id;
      const willData = willRecord.will_data as any;

      // Check if we have local changes that should take precedence
      const localData = useWillStore.getState().data;

      const hasLocalChanges = !!(
        localData.personalDetails?.fullName ||
        (localData.executors?.length ?? 0) > 0 ||
        (localData.beneficiaries?.length ?? 0) > 0
      );

      // Only overwrite with remote data if we DON'T have local changes
      // OR if the local will ID matches the remote will ID (same session)
      if (!hasLocalChanges || localWillId === willId) {
        // Load will data
        if (willData && Object.keys(willData).length > 0) {
          // Transform potentially nested DB data back to flat app state
          const flatWillData = transformToFlat(willData);

          // Inject status from the will record into the data object
          // This ensures the UI knows if the will is paid or not
          const dataToLoad = {
            ...flatWillData,
            status: willRecord.status
          };
          setData(dataToLoad);
        }

        // Restore progress
        const currentStep = willRecord.current_step;
        const completedSteps = willRecord.completed_steps;

        if (typeof currentStep === 'number') {
          useWillStore.getState().setStep(currentStep);
        }

        if (Array.isArray(completedSteps)) {
          // We need to manually set completed steps since the store action appends
          // But here we want to restore the exact state
          useWillStore.setState({ completedSteps });
        }
      } else {
        // CONFLICT DETECTED: We have local changes AND a remote will
        // Don't load remote data yet. Show conflict modal.
        setConflictRemoteData({
          willData,
          currentStep: willRecord.current_step,
          completedSteps: willRecord.completed_steps,
          status: willRecord.status
        });
        setConflictRemoteWillId(willId);
        setConflictRemoteDate(willRecord.updated_at || ''); // Assuming updated_at is available in select
        setShowConflictModal(true);

        // IMPORTANT: We temporarily DO NOT set the willId in the store yet
        // because if we do, useAutoSave might trigger and overwrite the remote will
        // before the user decides.
        // We will set it after they choose.
        return;
      }
    } else {
      // Create a new will
      const { data: newWill } = await supabase
        .from('wills')
        .insert({
          user_id: user.id,
          email: user.email || '',
          full_name: storedName || 'User',
          status: 'draft',
          will_data: {},
          eligibility_answers: storedEligibility || {},
        })
        .select('id')
        .single();

      willId = newWill?.id || '';
    }

    // Set auth state
    setAuth(user.id, user.email || '', willId);

    // Set email in will data
    if (user.email) {
      const currentData = useWillStore.getState().data;
      setData({
        personalDetails: {
          ...(currentData.personalDetails || {}),
          email: user.email,
        }
      });
    }

    // Auto-complete eligibility for authenticated users
    setEligibility(true);
    setCapability(true);
    setSuitability(true);
  };

  // Check for authenticated user returning from magic link OR guest access via willId
  useEffect(() => {
    if (!isHydrated) return;

    const searchParams = new URLSearchParams(window.location.search);
    const isAutoPayment = searchParams.get('auto_payment') === 'true';

    // Check for payment success
    if (searchParams.get('payment_success') === 'true') {
      setShowPaymentSuccessModal(true);
      // Clean up URL parameters
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('payment_success');
      newUrl.searchParams.delete('session_id');
      window.history.replaceState({}, '', newUrl.toString());
    }

    // If already authenticated, jump to Review & Summary
    if (isAuthenticated) {
      // Always force step 5 on load if authenticated, or if auto_payment requested
      if (isAutoPayment || useWillStore.getState().currentStep !== 5) {
        useWillStore.getState().setStep(5);
      }
      setIsCheckingAuth(false);
      return;
    }

    const supabase = createClient();
    let mounted = true;

    const initAuth = async () => {
      try {
        // 1. Check current session
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user && mounted) {
          await setupUser(session.user);
          // If auto_payment is requested, ensure we are on step 5
          const searchParams = new URLSearchParams(window.location.search);
          if (searchParams.get('auto_payment') === 'true') {
             useWillStore.getState().setStep(5);
          }
        } else {
          // Ensure any persisted auth flags are cleared when no active session exists
          clearAuth();
          // Check for willId in URL (Amendment Mode)
          const searchParams = new URLSearchParams(window.location.search);
          const willIdParam = searchParams.get('willId');
          const stepParam = searchParams.get('step');

          // Handle willIdParam (legacy guest access for amendments)
          if (willIdParam && mounted) {
            // Fetch will data directly (legacy guest access)
            try {
              const res = await fetch(`/api/will/data?willId=${willIdParam}`);
              if (res.ok) {
                const willData = await res.json();

                // Load data into store
                if (willData && Object.keys(willData).length > 0) {
                  // Ensure status is preserved/set from the API response
                  // Transform potentially nested DB data back to flat app state
                  const flatWillData = transformToFlat(willData);
                  
                  setData(flatWillData);
                  // We need to be careful here not to break RLS if we try to save later without a user.
                  // But for amendments, we might need a user session or a special token.
                  // For now, let's assume if they have the ID they can read. 
                  // Writing might require re-auth if we don't have a session.
                  // However, the user asked to "load their data".
                  setGuestAccess(willData.email || '', willIdParam);

                  // Set step if provided
                  if (stepParam) {
                    const stepIndex = parseInt(stepParam, 10);
                    if (!isNaN(stepIndex)) {
                      useWillStore.getState().setStep(stepIndex);
                    }
                  }

                  // Bypass eligibility
                  setEligibility(true);
                  setCapability(true);
                  setSuitability(true);
                }
              }
            } catch (e) {
              console.error('Failed to load guest will', e);
            }
          }
        }

        // 2. Listen for auth changes (e.g. after magic link hash processing)
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          if (event === 'SIGNED_IN' && session?.user && mounted) {
            await setupUser(session.user);
          }
          if (event === 'SIGNED_OUT' && mounted) {
            // Clear local store to avoid stale auth/data after explicit sign-out
            useWillStore.getState().reset();
          }
        });

        return subscription;
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        if (mounted) {
          setIsCheckingAuth(false);
        }
      }
    };

    const subPromise = initAuth();

    return () => {
      mounted = false;
      subPromise.then(sub => sub?.unsubscribe());
    };
  }, [isHydrated]);

  if (!isHydrated || isCheckingAuth) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="flex flex-col items-center pt-6 sm:pt-10 pb-20 px-4">
          <div className="form-container">
            <Skeleton className="h-10 w-64 rounded mx-auto mb-6" />
            <Skeleton className="h-64 w-full rounded" />
          </div>
        </main>
      </div>
    );
  }

  const renderContent = () => {
    // If authenticated, skip eligibility and go straight to the form
    // We check this first because setupUser sets eligibility to true, 
    // but just in case, we also check isAuthenticated
    if (isAuthenticated) {
      return <MultiStepForm />;
    }

    // Stage 1: Check Location
    if (isEligible === null) {
      return <EligibilityCheck />;
    }

    // Stage 1 Fail
    if (isEligible === false) {
      return (
        <div className="form-container pb-10">
          <div className="animate-fade-in-up max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl border border-foreground/5 shadow-[0_0_50px_-12px_rgba(53,140,127,0.15)] p-8 md:p-12 text-center">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-destructive mb-4">Request a Call back</h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We&apos;re sorry, but our Will writing service is currently only suitable for residents of England and Wales due
                to legal differences in other jurisdictions.
              </p>
              <div className="flex justify-center gap-4">
                <Link href="/" onClick={() => setEligibility(null)}>
                  <Button variant="outline" className="rounded-xl">Request a Call back</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Stage 2: Check Capability (if eligible)
    if (isCapable === null) {
      return <EligibilityCheck />;
    }

    // Stage 2 Fail
    if (isCapable === false) {
      return (
        <div className="form-container pb-10">
          <div className="animate-fade-in-up max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl border border-foreground/5 shadow-[0_0_50px_-12px_rgba(53,140,127,0.15)] p-8 md:p-12 text-center">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-destructive mb-4">Cannot Proceed</h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                To create a valid Will, you must be 18 or over and have the mental capacity to understand the document and its implications.
              </p>
              <div className="flex justify-center gap-4">
                <Link href="/" onClick={() => { setEligibility(null); setCapability(null); }}>
                  <Button variant="outline" className="rounded-xl">Return Home</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Stage 3: Check Suitability
    if (isSuitable === null) {
      return <EligibilityCheck />;
    }

    // Stage 3 Fail
    if (isSuitable === false) {
      return (
        <div className="form-container pb-10">
          <div className="animate-fade-in-up max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl border border-foreground/5 shadow-[0_0_50px_-12px_rgba(53,140,127,0.15)] p-8 md:p-12 text-center">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-destructive mb-4">Service Not Suitable</h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                This service is designed for simple wills. Based on your answers, you should speak to a solicitor. We can&apos;t
                provide a will in your situation.
              </p>
              <div className="flex justify-center gap-4">
                <Link href="/" onClick={() => { setEligibility(null); setCapability(null); setSuitability(null); }}>
                  <Button variant="outline" className="rounded-xl">Return Home</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Success: Show Form
    return <MultiStepForm />;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1 flex flex-col pt-2 pb-12 sm:pt-4 sm:pb-16">
        {renderContent()}
      </main>

      <ConflictModal
        isOpen={showConflictModal}
        remoteDate={conflictRemoteDate}
        onKeepLocal={() => {
          // User chose to keep local changes.
          // We just need to link the local session to the remote will ID
          // so that subsequent saves update the existing record.
          const { setAuth } = useWillStore.getState();
          // Re-set auth with the correct willId
          const user = createClient().auth.getUser().then(({ data }) => {
            if (data.user) {
              setAuth(data.user.id, data.user.email || '', conflictRemoteWillId);
              // Force a save immediately to update the remote with local
              // This is handled by useAutoSave usually, but we can trigger it or let it happen naturally
            }
          });
          setShowConflictModal(false);
        }}
        onLoadRemote={() => {
          // User chose to load remote data.
          // We overwrite local store with conflictRemoteData
          if (conflictRemoteData) {
            const { willData, currentStep, completedSteps, status } = conflictRemoteData;

            if (willData && Object.keys(willData).length > 0) {
              const flatWillData = transformToFlat(willData);
              // Ensure status is present so paid/submitted flows (e.g., amendment detection) work
              setData({
                ...flatWillData,
                status: status ?? flatWillData.status
              });
            }

            if (typeof currentStep === 'number') {
              useWillStore.getState().setStep(currentStep);
            }

            if (Array.isArray(completedSteps)) {
              useWillStore.setState({ completedSteps });
            }
          }

          // Link auth
          const { setAuth } = useWillStore.getState();
          const user = createClient().auth.getUser().then(({ data }) => {
            if (data.user) {
              setAuth(data.user.id, data.user.email || '', conflictRemoteWillId);
            }
          });

          setShowConflictModal(false);
        }}
      />
      
      <PaymentSuccessModal
        isOpen={showPaymentSuccessModal}
        onClose={() => setShowPaymentSuccessModal(false)}
      />

      {loginError && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 max-w-md shadow-lg z-50">
          <h3 className="font-semibold text-red-800">Login Failed</h3>
          <p className="text-sm text-red-700 mt-1">{loginError}</p>
          <button
            onClick={() => setLoginError(null)}
            className="mt-2 text-sm text-red-600 underline hover:text-red-800"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
